import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Calendar, MapPin } from 'lucide-react';
import { useStore } from '@/store/useStore';
import { formatDateTime, getRelativeTime, calculateAge } from '@/utils/dateUtils';
import type { Memory } from '@/types';

function MemoryCard({ memory }: { memory: Memory }) {
  const navigate = useNavigate();
  const { children, tags, toggleFavorite } = useStore();
  
  const child = children.find(c => c.id === memory.childId);
  const memoryTags = tags.filter(t => memory.tags.includes(t.id));
  const hasMedia = memory.media.length > 0;

  return (
    <div 
      onClick={() => navigate(`/memory/${memory.id}`)}
      className="bg-white rounded-2xl p-4 card-shadow mb-3 cursor-pointer active:scale-[0.98] transition-transform"
    >
      {/* 头部信息 */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
            style={{ backgroundColor: child?.color || '#ccc' }}
          >
            {child?.name.charAt(0) || '?'}
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-sm text-gray-800">{child?.name}</span>
              {child?.birthDate && child.type !== 'family' && (
                <span className="text-xs text-gray-500">
                  {calculateAge(child.birthDate, memory.createdAt)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <span>{getRelativeTime(memory.createdAt)}</span>
              <span>·</span>
              <span>{formatDateTime(memory.createdAt)}</span>
            </div>
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleFavorite(memory.id);
          }}
          className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
        >
          <Heart 
            className={`w-5 h-5 transition-colors ${
              memory.isFavorite 
                ? 'fill-red-500 text-red-500' 
                : 'text-gray-300'
            }`} 
          />
        </button>
      </div>

      {/* 内容 */}
      <p className="text-gray-700 text-sm leading-relaxed mb-3 line-clamp-4">
        {memory.content}
      </p>

      {/* 媒体预览 */}
      {hasMedia && (
        <div className={`grid gap-2 mb-3 ${
          memory.media.length === 1 ? 'grid-cols-1' : 
          memory.media.length === 2 ? 'grid-cols-2' : 'grid-cols-3'
        }`}>
          {memory.media.slice(0, 3).map((media, idx) => (
            <div 
              key={media.id}
              className="aspect-square rounded-lg overflow-hidden bg-gray-100 relative"
            >
              {media.type === 'image' ? (
                <img 
                  src={media.url} 
                  alt={media.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <video 
                  src={media.url}
                  className="w-full h-full object-cover"
                />
              )}
              {idx === 2 && memory.media.length > 3 && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-medium">
                  +{memory.media.length - 3}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 标签和位置 */}
      <div className="flex items-center gap-2 flex-wrap">
        {memoryTags.map(tag => (
          <span 
            key={tag.id}
            className="px-2 py-0.5 rounded-full text-xs"
            style={{ 
              backgroundColor: `${tag.color}20`,
              color: tag.color 
            }}
          >
            {tag.name}
          </span>
        ))}
        {memory.location && (
          <span className="flex items-center gap-0.5 text-xs text-gray-400">
            <MapPin className="w-3 h-3" />
            {memory.location}
          </span>
        )}
      </div>
    </div>
  );
}

export default function Home() {
  const { memories, currentChildId } = useStore();
  
  const filteredMemories = useMemo(() => {
    if (!currentChildId) return memories;
    return memories.filter(m => m.childId === currentChildId);
  }, [memories, currentChildId]);

  // 按日期分组
  const groupedMemories = useMemo(() => {
    const groups: Record<string, Memory[]> = {};
    filteredMemories.forEach(memory => {
      const date = memory.createdAt.split('T')[0];
      if (!groups[date]) groups[date] = [];
      groups[date].push(memory);
    });
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([date, items]) => ({ date, memories: items }));
  }, [filteredMemories]);

  if (memories.length === 0) {
    return (
      <div className="px-4 py-8">
        <div className="empty-state">
          <div className="w-24 h-24 rounded-full bg-primary-100 flex items-center justify-center mb-4">
            <Calendar className="w-12 h-12 text-primary-400" />
          </div>
          <p className="text-gray-500 mb-2">还没有记录</p>
          <p className="text-sm text-gray-400">点击右下角按钮，记录第一个美好瞬间</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-4">
      {/* 统计卡片 */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-white rounded-xl p-3 card-shadow">
          <p className="text-2xl font-bold text-primary-500">{filteredMemories.length}</p>
          <p className="text-xs text-gray-500">总记录</p>
        </div>
        <div className="bg-white rounded-xl p-3 card-shadow">
          <p className="text-2xl font-bold text-baby-blue">
            {filteredMemories.filter(m => m.isFavorite).length}
          </p>
          <p className="text-xs text-gray-500">收藏</p>
        </div>
        <div className="bg-white rounded-xl p-3 card-shadow">
          <p className="text-2xl font-bold text-baby-pink">
            {filteredMemories.filter(m => m.isCutePhrase).length}
          </p>
          <p className="text-xs text-gray-500">童言趣语</p>
        </div>
      </div>

      {/* 记忆列表 */}
      <div className="space-y-4">
        {groupedMemories.map(({ date, memories: dayMemories }) => (
          <div key={date}>
            <div className="flex items-center gap-2 mb-3 sticky top-0 bg-primary-50/95 py-2 z-10">
              <div className="w-2 h-2 rounded-full bg-primary-400" />
              <span className="text-sm font-medium text-gray-600">
                {new Date(date).toLocaleDateString('zh-CN', { 
                  month: 'long', 
                  day: 'numeric',
                  weekday: 'short'
                })}
              </span>
            </div>
            <div className="space-y-3">
              {dayMemories.map(memory => (
                <MemoryCard key={memory.id} memory={memory} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* 底部留白 */}
      <div className="h-20" />
    </div>
  );
}
